import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getSuratTugasList, createSuratTugas, updateSuratTugas, 
  deleteSuratTugas, completeSuratTugas, cancelSuratTugas,
  getUsersList, getSekolahList
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { 
  ChevronLeft, Plus, FileText, MapPin, Calendar, Clock, 
  User, Building, Loader2, Eye, Edit, Trash2, CheckCircle,
  XCircle, Upload, Download, AlertCircle
} from 'lucide-react';
import { toast } from '../ui/sonner';

const statusConfig = {
  active: { label: 'Aktif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Selesai', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Dibatalkan', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const SuratTugasPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [suratTugasList, setSuratTugasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSekolah, setFilterSekolah] = useState('all');
  
  // Form dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSuratTugas, setSelectedSuratTugas] = useState(null);
  
  // Lists for dropdowns
  const [guruList, setGuruList] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    guru_id: '',
    judul: '',
    keterangan: '',
    lokasi_nama: '',
    lokasi_latitude: '',
    lokasi_longitude: '',
    tanggal_mulai: '',
    tanggal_selesai: '',
    jam_mulai: '07:00',
    jam_selesai: '17:00',
    dokumen_base64: null,
    dokumen_filename: null
  });

  const canManage = ['admin_dinas', 'kepala_sekolah', 'pimpinan', 'pengawas', 'editor'].includes(user?.role);
  const canDelete = user?.role === 'admin_dinas';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSekolah !== 'all') params.sekolah_id = filterSekolah;
      
      const [suratTugas, users, sekolah] = await Promise.all([
        getSuratTugasList(params),
        canManage ? getUsersList({ role: 'guru' }).catch(() => []) : Promise.resolve([]),
        user?.role === 'admin_dinas' ? getSekolahList().catch(() => []) : Promise.resolve([])
      ]);
      
      setSuratTugasList(suratTugas);
      setGuruList(users);
      setSekolahList(sekolah);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSekolah, canManage, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      guru_id: '',
      judul: '',
      keterangan: '',
      lokasi_nama: '',
      lokasi_latitude: '',
      lokasi_longitude: '',
      tanggal_mulai: '',
      tanggal_selesai: '',
      jam_mulai: '07:00',
      jam_selesai: '17:00',
      dokumen_base64: null,
      dokumen_filename: null
    });
    setEditingId(null);
    setFormMode('create');
  };

  const openCreateDialog = () => {
    resetForm();
    setFormMode('create');
    setShowFormDialog(true);
  };

  const openEditDialog = (st) => {
    setFormData({
      guru_id: st.guru_id,
      judul: st.judul,
      keterangan: st.keterangan,
      lokasi_nama: st.lokasi_nama,
      lokasi_latitude: st.lokasi_latitude.toString(),
      lokasi_longitude: st.lokasi_longitude.toString(),
      tanggal_mulai: st.tanggal_mulai.split('T')[0],
      tanggal_selesai: st.tanggal_selesai.split('T')[0],
      jam_mulai: st.jam_mulai,
      jam_selesai: st.jam_selesai,
      dokumen_base64: null,
      dokumen_filename: null
    });
    setEditingId(st.id);
    setFormMode('edit');
    setShowFormDialog(true);
  };

  const openDetailDialog = (st) => {
    setSelectedSuratTugas(st);
    setShowDetailDialog(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          dokumen_base64: base64,
          dokumen_filename: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.guru_id || !formData.judul || !formData.lokasi_nama) {
      toast.error('Mohon lengkapi semua field yang wajib');
      return;
    }
    
    if (!formData.lokasi_latitude || !formData.lokasi_longitude) {
      toast.error('Mohon masukkan koordinat lokasi');
      return;
    }
    
    if (formMode === 'create' && !formData.dokumen_base64) {
      toast.error('Dokumen surat tugas wajib diupload');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        lokasi_latitude: parseFloat(formData.lokasi_latitude),
        lokasi_longitude: parseFloat(formData.lokasi_longitude),
        tanggal_mulai: formData.tanggal_mulai + 'T00:00:00Z',
        tanggal_selesai: formData.tanggal_selesai + 'T23:59:59Z'
      };
      
      if (formMode === 'create') {
        await createSuratTugas(payload);
        toast.success('Surat tugas berhasil dibuat');
      } else {
        const { guru_id, dokumen_base64, dokumen_filename, ...updatePayload } = payload;
        await updateSuratTugas(editingId, updatePayload);
        toast.success('Surat tugas berhasil diupdate');
      }
      
      setShowFormDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan surat tugas');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await completeSuratTugas(id);
      toast.success('Surat tugas ditandai selesai');
      fetchData();
    } catch (error) {
      toast.error('Gagal menandai selesai');
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelSuratTugas(id);
      toast.success('Surat tugas dibatalkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal membatalkan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus surat tugas ini?')) return;
    
    try {
      await deleteSuratTugas(id);
      toast.success('Surat tugas berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus surat tugas');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCurrentLocation = async () => {
    try {
      const { getCurrentPosition } = await import('../../lib/platformUtils');
      const position = await getCurrentPosition();
      setFormData(prev => ({
        ...prev,
        lokasi_latitude: position.coords.latitude.toFixed(6),
        lokasi_longitude: position.coords.longitude.toFixed(6)
      }));
      toast.success('Lokasi berhasil didapatkan');
    } catch (error) {
      toast.error('Gagal mendapatkan lokasi: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Kembali
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              Surat Tugas
            </h1>
            <p className="text-slate-600 mt-1">
              Kelola surat tugas untuk guru yang bertugas di luar sekolah
            </p>
          </div>
          
          {canManage && (
            <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Buat Surat Tugas
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto mb-6">
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Status:</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="completed">Selesai</SelectItem>
                    <SelectItem value="cancelled">Dibatalkan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {user?.role === 'admin_dinas' && sekolahList.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600">Sekolah:</Label>
                  <Select value={filterSekolah} onValueChange={setFilterSekolah}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Sekolah</SelectItem>
                      {sekolahList.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : suratTugasList.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada surat tugas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {suratTugasList.map((st) => {
              const StatusIcon = statusConfig[st.status]?.icon || FileText;
              return (
                <Card key={st.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-800 truncate">
                                {st.judul}
                              </h3>
                              <Badge className={statusConfig[st.status]?.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig[st.status]?.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mb-2">
                              {st.nomor_surat}
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {st.guru_nama}
                              </span>
                              <span className="flex items-center gap-1">
                                <Building className="w-4 h-4" />
                                {st.sekolah_nama}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {st.lokasi_nama}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(st.tanggal_mulai)} - {formatDate(st.tanggal_selesai)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {st.jam_mulai} - {st.jam_selesai}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openDetailDialog(st)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detail
                        </Button>
                        
                        {canManage && st.status === 'active' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(st)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleComplete(st.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Selesai
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleCancel(st.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Batalkan
                            </Button>
                          </>
                        )}
                        
                        {canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDelete(st.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Buat Surat Tugas Baru' : 'Edit Surat Tugas'}
            </DialogTitle>
            <DialogDescription>
              Isi informasi surat tugas untuk guru yang bertugas di luar sekolah
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Guru Selection */}
            {formMode === 'create' && (
              <div className="space-y-2">
                <Label htmlFor="guru_id">Guru *</Label>
                <Select 
                  value={formData.guru_id} 
                  onValueChange={(v) => setFormData(prev => ({...prev, guru_id: v}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih guru" />
                  </SelectTrigger>
                  <SelectContent>
                    {guruList.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.nama} - {g.sekolah_nama || 'Belum ada sekolah'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Judul */}
            <div className="space-y-2">
              <Label htmlFor="judul">Judul Kegiatan *</Label>
              <Input
                id="judul"
                value={formData.judul}
                onChange={(e) => setFormData(prev => ({...prev, judul: e.target.value}))}
                placeholder="Contoh: Workshop Kurikulum Merdeka"
              />
            </div>
            
            {/* Keterangan */}
            <div className="space-y-2">
              <Label htmlFor="keterangan">Keterangan</Label>
              <Textarea
                id="keterangan"
                value={formData.keterangan}
                onChange={(e) => setFormData(prev => ({...prev, keterangan: e.target.value}))}
                placeholder="Deskripsi kegiatan..."
                rows={3}
              />
            </div>
            
            {/* Lokasi */}
            <div className="space-y-2">
              <Label>Lokasi Kegiatan *</Label>
              <Input
                value={formData.lokasi_nama}
                onChange={(e) => setFormData(prev => ({...prev, lokasi_nama: e.target.value}))}
                placeholder="Nama tempat, contoh: Aula Dinas Pendidikan"
                className="mb-2"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  step="any"
                  value={formData.lokasi_latitude}
                  onChange={(e) => setFormData(prev => ({...prev, lokasi_latitude: e.target.value}))}
                  placeholder="Latitude"
                />
                <Input
                  type="number"
                  step="any"
                  value={formData.lokasi_longitude}
                  onChange={(e) => setFormData(prev => ({...prev, lokasi_longitude: e.target.value}))}
                  placeholder="Longitude"
                />
              </div>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={getCurrentLocation}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Gunakan Lokasi Saat Ini
              </Button>
            </div>
            
            {/* Tanggal */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tanggal_mulai">Tanggal Mulai *</Label>
                <Input
                  id="tanggal_mulai"
                  type="date"
                  value={formData.tanggal_mulai}
                  onChange={(e) => setFormData(prev => ({...prev, tanggal_mulai: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_selesai">Tanggal Selesai *</Label>
                <Input
                  id="tanggal_selesai"
                  type="date"
                  value={formData.tanggal_selesai}
                  onChange={(e) => setFormData(prev => ({...prev, tanggal_selesai: e.target.value}))}
                />
              </div>
            </div>
            
            {/* Jam */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jam_mulai">Jam Mulai *</Label>
                <Input
                  id="jam_mulai"
                  type="time"
                  value={formData.jam_mulai}
                  onChange={(e) => setFormData(prev => ({...prev, jam_mulai: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jam_selesai">Jam Selesai *</Label>
                <Input
                  id="jam_selesai"
                  type="time"
                  value={formData.jam_selesai}
                  onChange={(e) => setFormData(prev => ({...prev, jam_selesai: e.target.value}))}
                />
              </div>
            </div>
            
            {/* Dokumen Upload */}
            <div className="space-y-2">
              <Label htmlFor="dokumen">Dokumen Surat Tugas {formMode === 'create' && '*'}</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  id="dokumen"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label 
                  htmlFor="dokumen"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  {formData.dokumen_filename ? (
                    <span className="text-sm text-green-600 font-medium">
                      {formData.dokumen_filename}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-500">
                      Klik untuk upload PDF, JPG, atau PNG (max 5MB)
                    </span>
                  )}
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowFormDialog(false)}
              >
                Batal
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {formMode === 'create' ? 'Buat Surat Tugas' : 'Simpan Perubahan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detail Surat Tugas</DialogTitle>
          </DialogHeader>
          
          {selectedSuratTugas && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={statusConfig[selectedSuratTugas.status]?.color}>
                  {statusConfig[selectedSuratTugas.status]?.label}
                </Badge>
                <span className="text-sm text-slate-500">
                  {selectedSuratTugas.nomor_surat}
                </span>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg">{selectedSuratTugas.judul}</h3>
                <p className="text-sm text-slate-600 mt-1">{selectedSuratTugas.keterangan}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Guru</Label>
                  <p className="font-medium">{selectedSuratTugas.guru_nama}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Sekolah</Label>
                  <p className="font-medium">{selectedSuratTugas.sekolah_nama}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Lokasi Kegiatan</Label>
                  <p className="font-medium">{selectedSuratTugas.lokasi_nama}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Koordinat</Label>
                  <p className="font-medium text-xs">
                    {selectedSuratTugas.lokasi_latitude}, {selectedSuratTugas.lokasi_longitude}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Tanggal</Label>
                  <p className="font-medium">
                    {formatDate(selectedSuratTugas.tanggal_mulai)} - {formatDate(selectedSuratTugas.tanggal_selesai)}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Jam</Label>
                  <p className="font-medium">
                    {selectedSuratTugas.jam_mulai} - {selectedSuratTugas.jam_selesai}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Dibuat Oleh</Label>
                  <p className="font-medium">{selectedSuratTugas.created_by_nama}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Tanggal Dibuat</Label>
                  <p className="font-medium">{formatDate(selectedSuratTugas.created_at)}</p>
                </div>
              </div>
              
              {selectedSuratTugas.dokumen_url && (
                <div>
                  <Label className="text-slate-500">Dokumen</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => window.open(selectedSuratTugas.dokumen_url, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Lihat/Download Dokumen
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuratTugasPage;
